/* eslint-disable react/prop-types */
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useDispatchToStore } from '@hooks/reduxHooks';
import H3 from '@base/H3/H3';
import Drawer from '@base/Drawer/Drawer';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import InfiniteList from '@comp/InfiniteList/InfiniteList';
import StoryItem from '@comp/StoryItem/StoryItem';
import Dropdown from '@comp/Dropdown/Dropdown';
import {
  fetchCourseUpdates,
  fetchUpdateFilters,
  updateFilters
} from '@actions/courses';

const Header = styled(H3)`
  grid-column: span 10;
  margin-bottom: 24px;
  color: var(--color-text-primary);
`;

const Loader = styled(CircularLoader)`
  grid-column: 5/6;
`;

const BatchUpdates = ({ batchId, showUpdates, onCloseUpdates, channelId }) => {
  const dispatch = useDispatchToStore();
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
  const [selectedStory, setSelectedStory] = useState(null);

  useEffect(() => {
    dispatch(fetchCourseUpdates(batchId, true, true, true));
    dispatch(updateFilters(null, '', true));
    dispatch(fetchUpdateFilters(channelId));
  }, [dispatch, batchId, channelId]);

  useEffect(() => {
    setTimeout(() => {
      setIsInitialLoadDone(showUpdates);
    }, 10);
  }, [showUpdates]);

  const channelStories = useSelector(
    (state) => state.paginator.courseUpdates[batchId]
  );

  const selectedFilter = useSelector((state) => state.updates.selectedFilter);

  const selectedEducator = useSelector(
    (state) => state.updates.selectedEducatorFilter
  );

  const filters = useSelector((state) => state.updates.filters);
  const educatorFilters = useSelector((state) => state.updates.educatorFilters);

  const onFilterClick = (filter) => {
    dispatch(updateFilters(filter, 'other'));
    const requiredFilter = [
      { filter_type: filter.label, filter_value: filter.value }
    ];
    if (selectedEducator.value !== 'all') {
      requiredFilter.push({
        filter_type: 'Educator',
        filter_value: selectedEducator.value
      });
    }
    dispatch(fetchCourseUpdates(batchId, true, true, true, requiredFilter));
  };

  const onEducatorClick = (filter) => {
    dispatch(updateFilters(filter, 'educator'));
    const requiredFilter = [
      {
        filter_type: selectedFilter?.label,
        filter_value: selectedFilter?.value
      }
    ];
    if (filter.value !== 'all') {
      requiredFilter.push({
        filter_type: 'Educator',
        filter_value: filter.value
      });
    }
    dispatch(fetchCourseUpdates(batchId, true, true, true, requiredFilter));
  };

  const loadMoreStories = () =>
    dispatch(fetchCourseUpdates(batchId, true, false, true));

  const handleOnPressBack = () => setSelectedStory(null);

  const storiesCount = channelStories?.results?.length;
  const isFetching = channelStories?.isFetching;

  const renderFilters = () => {
    if ((isFetching && storiesCount < 1) || selectedStory || storiesCount < 1)
      return;
    return (
      <>
        <Dropdown
          options={filters}
          selectedOption={selectedFilter}
          onOptionClick={onFilterClick}
          gridColumn="span 5"
        />
        <Dropdown
          options={educatorFilters}
          selectedOption={selectedEducator}
          onOptionClick={onEducatorClick}
          gridColumn="span 5"
        />
      </>
    );
  };

  const renderUpdate = (story) => {
    if (!story) return null;
    return (
      <StoryItem
        story={story}
        selectedStory={selectedStory}
        channelId={batchId}
        setSelectedStory={setSelectedStory}
        batch
      />
    );
  };

  const getHeader = () => {
    if (isFetching && storiesCount < 1) return <Loader />;
    let text = 'No updates available';
    if (storiesCount) text = 'Updates';
    return <Header>{text}</Header>;
  };

  return (
    <Drawer
      drawerState={showUpdates}
      onClose={onCloseUpdates}
      showBackButton={selectedStory}
      hideCloseButton={selectedStory}
      onPressBack={handleOnPressBack}
    >
      {getHeader()}
      {renderFilters()}
      {renderUpdate(selectedStory)}
      {isInitialLoadDone && storiesCount && !selectedStory ? (
        <InfiniteList
          data={channelStories}
          loadMore={loadMoreStories}
          _renderItem={renderUpdate}
          scrollElement={document.getElementById('DrawerPaper')}
        />
      ) : null}
    </Drawer>
  );
};

export default BatchUpdates;
